import { Popover } from '@headlessui/react';
import { FunnelIcon } from '@heroicons/react/20/solid';
import React, { ReactNode, isValidElement, useCallback, useEffect, useRef, useState } from 'react';
import ButtonV2 from '../button/ButtonV2';
import CheckboxFilter, { CheckboxFilterProps } from './filters/CheckboxFilter';
import { useSearchParams } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import FilterV2Provider, { useFilterContext } from './FilterV2Context';
import FilterPills from './FilterPills';
import TextFilter, { TextFilterProps } from './filters/TextFilter';
import * as qs from 'qs';
import {
    sanitizeCheckboxQueryString,
    sanitizeDateQueryString,
    sanitizeNumericQueryString,
    sanitizeSelectQueryString,
    sanitizeTextQueryString,
    sanitizeToggleQueryString,
} from './utils';
import NumericFilter, { NumericFilterProps } from './filters/NumericFilter';
import DateFilter from './filters/DateFilter';
import ToggleFilter, { ToggleFilterProps } from './filters/ToggleFilter';
import SelectFilter, { SelectFilterProps } from './filters/SelectFilter';
import { FilterItem, FinalizedQuery } from './filterV2.types';

type FilterV2Props = {
    size?: 'xs' | 'sm';
    children: ReactNode | ReactNode[];
    isDisabled?: boolean;
    onChange: (vals: { query: FinalizedQuery; queryString: string }) => void;
};

function Filter({ size = 'sm', children, isDisabled, onChange }: FilterV2Props): JSX.Element {
    const [search] = useSearchParams();
    const ctx = useFilterContext();
    const [registering, setRegistering] = useState(true);

    /**
     * Get each child's filter type and register it against it's specific filter type.
     * This function will also add any default values that are found in the url query.
     */
    const registerChildren = useCallback(
        function registerChildren(): void {
            setRegistering(true);
            React.Children.forEach(children, (child) => {
                if (isValidElement(child) && typeof child.type !== 'string') {
                    const query = qs.parse(search.toString());
                    switch (child.type.name) {
                        case 'CheckboxFilter': {
                            const { id, label, options } = child.props as CheckboxFilterProps;
                            const values = sanitizeCheckboxQueryString(id, options, query);
                            ctx.register({ id, label, type: 'checkbox', values, options });
                            break;
                        }
                        case 'TextFilter': {
                            const { id, label } = child.props as TextFilterProps;
                            const { condition, values } = sanitizeTextQueryString(id, query);
                            ctx.register({ id, label, type: 'text', condition, values });
                            break;
                        }
                        case 'NumericFilter': {
                            const { id, label, isPercentage = false } = child.props as NumericFilterProps;
                            const { condition, values } = sanitizeNumericQueryString(id, query);
                            ctx.register({ id, label, type: 'numeric', condition, values, isPercentage });
                            break;
                        }
                        case 'DateFilter': {
                            const { id, label } = child.props as NumericFilterProps;
                            const { condition, values } = sanitizeDateQueryString(id, query);
                            ctx.register({ id, label, type: 'date', condition, values });
                            break;
                        }
                        case 'ToggleFilter': {
                            const { id, label, prefix = 'has' } = child.props as ToggleFilterProps;
                            const active = sanitizeToggleQueryString(id, query);
                            ctx.register({ id, label, type: 'toggle', active, prefix });
                            break;
                        }
                        case 'SelectFilter': {
                            const { id, label, options } = child.props as SelectFilterProps;
                            const url = sanitizeSelectQueryString(id, query);
                            const values = options.filter((o) => url.includes(o.value.toString()));
                            ctx.register({ id, label, type: 'select', values, options });
                            break;
                        }
                        default:
                            break;
                    }
                }
            });
            setRegistering(false);
        },
        [children, ctx, search]
    );

    useEffect(() => {
        onChange(ctx.query);
    }, [ctx.query, onChange]);

    useEffect(() => {
        registerChildren();
    }, [registerChildren]);

    /**
     * Listen for a change in the url to update filter selections.
     *
     */
    useEffect(() => {
        // This is needed to prevent an infinite loop
        if (search.toString() !== ctx.currentSearch.toString() && !registering) {
            const query = qs.parse(search.toString());

            const filters = ctx.getAll();

            const draft: Map<string, FilterItem> = new Map();

            for (const filter of filters) {
                const matchingQuery = query[filter.id];

                if (!matchingQuery) {
                    if (filter.type === 'toggle') {
                        draft.set(filter.id, { ...filter, active: false });
                    } else {
                        draft.set(filter.id, { ...filter, values: [] });
                    }
                } else {
                    switch (filter.type) {
                        case 'checkbox': {
                            const values = sanitizeCheckboxQueryString(filter.id, filter.options, query);
                            // ctx.updateValues('checkbox', filter.id, { values }, false);
                            draft.set(filter.id, { ...filter, values });
                            break;
                        }
                        case 'text': {
                            const { condition, values } = sanitizeTextQueryString(filter.id, query);
                            // ctx.updateValues('text', filter.id, { condition, values }, false);
                            draft.set(filter.id, { ...filter, values, condition });
                            break;
                        }
                        case 'numeric': {
                            const { condition, values } = sanitizeNumericQueryString(filter.id, query);
                            // ctx.updateValues('numeric', filter.id, { condition, values }, false);
                            draft.set(filter.id, { ...filter, values, condition });
                            break;
                        }
                        case 'toggle': {
                            const active = sanitizeToggleQueryString(filter.id, query);
                            // ctx.updateValues('toggle', filter.id, { active }, false);
                            draft.set(filter.id, { ...filter, active });
                            break;
                        }
                        case 'select': {
                            const url = sanitizeSelectQueryString(filter.id, query);
                            const values = filter.options.filter((o) => url.includes(o.value.toString()));
                            // ctx.updateValues('select', filter.id, { values }, false);
                            draft.set(filter.id, { ...filter, values });
                            break;
                        }
                        default:
                            break;
                    }
                }
            }

            ctx.finalize(draft);
        }
    }, [ctx, search]);

    return (
        <div>
            <Popover as="div">
                {({ open }): JSX.Element => {
                    return (
                        <>
                            <Popover.Button as="div" className="w-fit">
                                <ButtonV2 type="secondary" label="Filter" leftIcon={<FunnelIcon />} size={size} isDisabled={isDisabled} />
                            </Popover.Button>
                            <AnimatePresence
                                initial={false}
                                onExitComplete={(): void => {
                                    ctx.finalize();
                                }}
                            >
                                {open && (
                                    <Popover.Panel
                                        className="bg-white shadow-md rounded-md min-w-[250px] outline-none absolute mt-2 z-50 dark:border-white/[0.05] dark:text-gray-200 dark:bg-white/[0.05]"
                                        static
                                        as={motion.div}
                                    >
                                        <div className="relative w-full ">
                                            <div className="p-3 border-b dark:border-white/[0.05]">
                                                <p className="text-sm text-gray-900 font-medium dark:text-gray-200">Filters</p>
                                            </div>
                                            <div className="w-full max-h-[300px] overflow-scroll">{children}</div>
                                        </div>
                                    </Popover.Panel>
                                )}
                            </AnimatePresence>
                        </>
                    );
                }}
            </Popover>
            <div className="mt-2 absolute">
                <FilterPills>{children}</FilterPills>
            </div>
        </div>
    );
}

function _FilterV2(props: FilterV2Props): JSX.Element {
    return (
        <FilterV2Provider>
            <Filter {...props} />
        </FilterV2Provider>
    );
}

const FilterV2 = Object.assign(_FilterV2, {
    CheckboxFilter,
    TextFilter,
    NumericFilter,
    DateFilter,
    ToggleFilter,
    SelectFilter,
});

export default FilterV2;
